import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import type { AttributeValueAdmin } from './types';
import { productAttributesEndpoint } from './use-get-attribute-dictionary-api';

export interface UpdateAttributeValuePayload {
  value?: string;
  label?: string;
  synonyms?: string[] | null;
  sortOrder?: number;
  isActive?: boolean;
}

interface Params {
  id: number;
  data: UpdateAttributeValuePayload;
}

type TData = AttributeValueAdmin;
type TError = AxiosError;
type TVariables = Params;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// PUT ums/v1/cms/master-product-attributes/values/:id
export function useUpdateAttributeValueApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, data }: TVariables): Promise<TData> => {
    try {
      const response = await http.put(`${productAttributesEndpoint}/values/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${productAttributesEndpoint}/values/update`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([`${productAttributesEndpoint}/types`]);
      queryClient.invalidateQueries([`${productAttributesEndpoint}/dictionary`]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
